import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DashboardOutlined,
  SoundOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  AuditOutlined,
  ApiOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import authService from '../services/authService';

const { Sider } = Layout;
const { Title } = Typography;

const Sidebar = ({ collapsed, onCollapse, selectedKey, onMenuSelect }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user info
  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  
  const handleMenuClick = ({ key }) => {
    // Map menu keys to routes
    const routeMap = {
      'dashboard': '/dashboard',
      'campaigns': '/dashboard/campaigns',
      'questionnaires': '/dashboard/questionnaires',
      'business-units': '/dashboard/business-units',
      'settings-business-units': '/dashboard/settings/business-units',
      'team': '/dashboard/team',
      'team-users': '/dashboard/team',
      'team-groups': '/dashboard/team-groups',
      'audit-trail': '/dashboard/audit-trail',
      'api-activity': '/dashboard/api-activity',
      'settings': '/dashboard/settings',
      'reports': '/dashboard/reports',
      'getting-started': '/dashboard/getting-started',
    };
    
    const route = routeMap[key];
    if (route) {
      navigate(route);
    }
    
    // Keep the old callback for any other functionality
    if (onMenuSelect) {
      onMenuSelect(key);
    }
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'campaigns',
      icon: <SoundOutlined />,
      label: 'Campaigns',
    },
    {
      key: 'questionnaires',
      icon: <QuestionCircleOutlined />,
      label: 'Quizzes',
    },
    {
      key: 'business-units',
      icon: <TeamOutlined />,
      label: 'Organisations',
    },
    {
      key: 'team',
      icon: <UserOutlined />,
      label: 'TapLearnGo Team',
      children: [
        {
          key: 'team-users',
          icon: <UserOutlined />,
          label: 'Users',
        },
        {
          key: 'team-groups',
          icon: <UsergroupAddOutlined />,
          label: 'Groups',
        },
        {
          key: 'audit-trail',
          icon: <AuditOutlined />,
          label: 'Audit Trail',
        },
        {
          key: 'api-activity',
          icon: <ApiOutlined />,
          label: 'API Activity',
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      children: [
        {
          key: 'settings-business-units',
          icon: <TeamOutlined />,
          label: 'Organisation Settings',
        },
      ],
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: 'Reports',
    },
  ];

  // Filter menu items based on user role
  const filterMenuByRole = (menuItems, userType) => {
    console.log('Filtering menu for userType:', userType);
    
    if (!userType) {
      console.log('No userType found, showing all menu items');
      return menuItems; // If no user type, show all (fallback)
    }
    
    // Super Admin has access to everything
    if (userType === 'superAdmin') {
      console.log('Super admin detected, showing all menu items');
      return menuItems;
    }
    
    // Manager has limited access
    if (userType === 'manager') {
      console.log('Manager detected, filtering menu items');
      const filteredItems = menuItems.filter(item => {
        // Allow these menu items for managers
        const allowedKeys = [
          'dashboard',
          'business-units', // Managers can access organizations (limited to their assigned ones)
          'settings'
        ];
        
        return allowedKeys.includes(item.key);
      }).map(item => {
        // If it's the organizations menu, filter its children too
        if (item.key === 'business-units' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => 
              ['business-units-list'].includes(child.key) // Only allow list, not settings
            )
          };
        }
        
        return item;
      });
      console.log('Filtered menu items for manager:', filteredItems.map(item => item.key));
      return filteredItems;
    }
    
    // Participant has very limited access
    if (userType === 'participant') {
      console.log('Participant detected, showing limited menu items');
      const filteredItems = menuItems.filter(item => {
        const allowedKeys = [
          'dashboard'
        ];
        return allowedKeys.includes(item.key);
      });
      console.log('Filtered menu items for participant:', filteredItems.map(item => item.key));
      return filteredItems;
    }
    
    // Default: return all items if userType is not recognized
    console.log('Unknown userType, showing all menu items');
    return menuItems;
  };

  // Get filtered menu items based on current user role
  const filteredMenuItems = filterMenuByRole(menuItems, currentUser?.userType);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      width={250}
      style={{
        height: '100vh',
        position: 'fixed',
        left: isMobile && collapsed ? '-250px' : 0,
        top: 0,
        background: '#fff',
        borderRight: '1px solid #f0f0f0',
        zIndex: isMobile ? 1000 : 100,
        transition: 'left 0.2s',
      }}
    >
      <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
        {!collapsed && (
          <div style={{ textAlign: 'center' }}>
            <img 
              src="/logo.png" 
              alt="TapLearnGo Logo"
              style={{ 
                maxWidth: '120px', 
                height: 'auto',
                backgroundColor: 'transparent'
              }}
              onError={(e) => {
                // Fallback to text if logo fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Title level={4} style={{ color: '#0066cc', margin: 0, display: 'none' }}>
              TAPLEARNGO
            </Title>
          </div>
        )}
        {collapsed && (
          <div style={{ textAlign: 'center', color: '#0066cc', fontSize: '18px', fontWeight: 'bold' }}>
            TLG
          </div>
        )}
      </div>
      
      <div style={{ padding: '16px 0' }}>
        {!collapsed && (
          <div style={{ 
            padding: '0 16px', 
            marginBottom: '16px', 
            display: 'flex', 
            alignItems: 'center',
            fontSize: '14px',
            color: '#666'
          }}>
            <UserOutlined style={{ marginRight: '8px' }} />
            TapLearnGo System
          </div>
        )}
        
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{ border: 'none' }}
          items={filteredMenuItems}
          onClick={handleMenuClick}
        />
      </div>
    </Sider>
  );
};

export default Sidebar;